/* eslint-disable sonarjs/cognitive-complexity */

/* eslint-disable unicorn/prefer-string-slice */

/* eslint-disable unicorn/prefer-includes */
import React from 'react'

import {
  ChangeMeta,
  FormatInputValueFunction,
  InputAttributes,
  NumberFormatBaseProps,
  NumericFormatProps,
  RemoveFormattingFunction,
  SourceType,
} from './types'
import {
  applyThousandSeparator,
  charIsNumber,
  escapeRegExp,
  fixLeadingZero,
  getDefaultChangeMeta,
  isNanValue,
  isNil,
  limitToScale,
  noop,
  roundToPrecision,
  setCaretPosition,
  splitDecimal,
  toNumericString,
  useInternalValues,
} from './utils'

export function format<BaseType = InputAttributes>(
  numStr: string,
  props: NumericFormatProps<BaseType>,
) {
  const {
    decimalScale,
    fixedDecimalScale,
    prefix = '',
    suffix = '',
    allowNegative,
    thousandsGroupStyle = 'thousand',
  } = props

  if (numStr === '' || numStr === '-') {
    return numStr
  }

  const { thousandSeparator, decimalSeparator } = getSeparators(props)

  const hasDecimalSeparator =
    // eslint-disable-next-line unicorn/prefer-includes
    (decimalScale !== 0 && numStr.indexOf('.') !== -1) ||
    (decimalScale && fixedDecimalScale)

  // eslint-disable-next-line prefer-const
  let { beforeDecimal, afterDecimal, addNegation } = splitDecimal(
    numStr,
    allowNegative,
  ) // eslint-disable-line prefer-const

  if (decimalScale !== undefined) {
    afterDecimal = limitToScale(afterDecimal, decimalScale, !!fixedDecimalScale)
  }

  if (thousandSeparator) {
    beforeDecimal = applyThousandSeparator(
      beforeDecimal,
      thousandSeparator,
      thousandsGroupStyle,
    )
  }

  if (prefix) beforeDecimal = prefix + beforeDecimal
  if (suffix) afterDecimal = afterDecimal + suffix

  if (addNegation) beforeDecimal = '-' + beforeDecimal

  numStr =
    beforeDecimal +
    ((hasDecimalSeparator && decimalSeparator) || '') +
    afterDecimal

  return numStr
}

function getSeparators<BaseType = InputAttributes>(
  props: NumericFormatProps<BaseType>,
) {
  const { decimalSeparator = '.' } = props
  let { thousandSeparator, allowedDecimalSeparators } = props
  if (thousandSeparator === true) {
    thousandSeparator = ','
  }
  if (!allowedDecimalSeparators) {
    allowedDecimalSeparators = [decimalSeparator, '.']
  }

  return {
    decimalSeparator,
    thousandSeparator,
    allowedDecimalSeparators,
  }
}

function handleNegation(value = '', allowNegative: boolean) {
  const negationRegex = new RegExp('(-)')
  const doubleNegationRegex = new RegExp('(-)(.)*(-)')

  const hasNegation = negationRegex.test(value)

  const removeNegation = doubleNegationRegex.test(value)

  value = value.replace(/-/g, '')

  if (hasNegation && !removeNegation && allowNegative) {
    value = '-' + value
  }

  return value
}

function getNumberRegex(decimalSeparator: string, global: boolean) {
  return new RegExp(
    `(^-)|[0-9]|${escapeRegExp(decimalSeparator)}`,
    global ? 'g' : undefined,
  )
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function removeFormatting<BaseType = InputAttributes>(
  value: string,
  changeMeta: ChangeMeta = getDefaultChangeMeta(value),
  props: NumericFormatProps<BaseType>,
) {
  const { allowNegative, prefix = '', suffix = '', decimalScale } = props
  const { from, to } = changeMeta
  let { start, end } = to
  const { allowedDecimalSeparators, decimalSeparator } = getSeparators(props)

  const isBeforeDecimalSeparator = value[end] === decimalSeparator

  if (
    charIsNumber(value) &&
    (value === prefix || value === suffix) &&
    changeMeta.lastValue === ''
  ) {
    return value
  }

  if (
    end - start === 1 &&
    allowedDecimalSeparators.indexOf(value[start]) !== -1
  ) {
    const separator = decimalScale === 0 ? '' : decimalSeparator
    value =
      value.substring(0, start) +
      separator +
      value.substring(start + 1, value.length)
  }

  const stripNegation = (value: string, start: number, end: number) => {
    let hasNegation = false
    let hasDoubleNegation = false

    if (prefix.startsWith('-')) {
      hasNegation = false
    } else if (value.startsWith('--')) {
      hasNegation = false
      hasDoubleNegation = true
    } else if (suffix.startsWith('-') && value.length === suffix.length) {
      hasNegation = false
    } else if (value[0] === '-') {
      hasNegation = true
    }

    let charsToRemove = hasNegation ? 1 : 0
    if (hasDoubleNegation) charsToRemove = 2

    if (charsToRemove) {
      value = value.substring(charsToRemove)

      start -= charsToRemove
      end -= charsToRemove
    }

    return { value, start, end, hasNegation }
  }

  const toMetadata = stripNegation(value, start, end)
  const { hasNegation } = toMetadata
  ;({ value, start, end } = toMetadata)

  const {
    start: fromStart,
    end: fromEnd,
    value: lastValue,
  } = stripNegation(changeMeta.lastValue, from.start, from.end)

  const updatedSuffixPart = value.substring(start, end)
  if (
    value.length > 0 &&
    lastValue.length > 0 &&
    (fromStart > lastValue.length - suffix.length || fromEnd < prefix.length) &&
    !(updatedSuffixPart && suffix.startsWith(updatedSuffixPart))
  ) {
    value = lastValue
  }

  let startIndex = 0
  if (value.startsWith(prefix)) startIndex += prefix.length
  else if (start < prefix.length) startIndex = start
  value = value.substring(startIndex)

  end -= startIndex

  let endIndex = value.length
  const suffixStartIndex = value.length - suffix.length

  if (value.endsWith(suffix)) endIndex = suffixStartIndex
  else if (end > suffixStartIndex) endIndex = end
  else if (end > value.length - suffix.length) endIndex = end

  value = value.substring(0, endIndex)

  value = handleNegation(
    hasNegation ? `-${value}` : value,
    Boolean(allowNegative),
  )

  value = (value.match(getNumberRegex(decimalSeparator, true)) || []).join('')

  const firstIndex = value.indexOf(decimalSeparator)
  value = value.replace(
    new RegExp(escapeRegExp(decimalSeparator), 'g'),
    (match, index) => {
      return index === firstIndex ? '.' : ''
    },
  )

  const { beforeDecimal, afterDecimal, addNegation } = splitDecimal(
    value,
    allowNegative,
  )

  if (
    // eslint-disable-next-line unicorn/consistent-destructuring
    to.end - to.start < from.end - from.start &&
    beforeDecimal === '' &&
    isBeforeDecimalSeparator &&
    !Number.parseFloat(afterDecimal)
  ) {
    value = addNegation ? '-' : ''
  }

  return value
}

export function getCaretBoundary<BaseType = InputAttributes>(
  formattedValue: string,
  props: NumericFormatProps<BaseType>,
) {
  const { prefix = '', suffix = '' } = props
  const boundaryAry = Array.from({ length: formattedValue.length + 1 }).map(
    () => true,
  )

  const hasNegation = formattedValue[0] === '-'

  boundaryAry.fill(false, 0, prefix.length + (hasNegation ? 1 : 0))

  const valLn = formattedValue.length
  boundaryAry.fill(false, valLn - suffix.length + 1, valLn + 1)

  return boundaryAry
}

function validateAndUpdateProps<BaseType = InputAttributes>(
  props: NumericFormatProps<BaseType>,
) {
  const { thousandSeparator, decimalSeparator } = getSeparators(props)
  // eslint-disable-next-line prefer-const
  let { prefix = '', allowNegative = true } = props

  if (thousandSeparator === decimalSeparator) {
    throw new Error(`
        Decimal separator can't be same as thousand separator.
        thousandSeparator: ${thousandSeparator} (thousandSeparator = {true} is same as thousandSeparator = ",")
        decimalSeparator: ${decimalSeparator} (default value for decimalSeparator is .)
     `)
  }

  if (prefix.startsWith('-') && allowNegative) {
    allowNegative = false
  }

  return {
    ...props,
    allowNegative,
  }
}

export function useNumericFormat<BaseType = InputAttributes>(
  props: NumericFormatProps<BaseType>,
): NumberFormatBaseProps<BaseType> {
  props = validateAndUpdateProps(props)

  const {
    decimalSeparator = '.',
    allowedDecimalSeparators,
    thousandsGroupStyle,
    suffix,
    allowNegative,
    allowLeadingZeros,
    onKeyDown = noop,
    staticSeparator,
    onBlur = noop,
    thousandSeparator,
    decimalScale,
    fixedDecimalScale,
    prefix = '',
    defaultValue,
    value,
    valueIsNumericString,
    onValueChange,
    ...restProps
  } = props

  const _format: FormatInputValueFunction = (numStr) => format(numStr, props)

  const _removeFormatting: RemoveFormattingFunction = (
    inputValue,
    changeMeta,
  ) => removeFormatting(inputValue, changeMeta, props)

  let _valueIsNumericString = valueIsNumericString

  if (!isNil(value)) {
    _valueIsNumericString = valueIsNumericString ?? typeof value === 'number'
  } else if (!isNil(defaultValue)) {
    _valueIsNumericString =
      valueIsNumericString ?? typeof defaultValue === 'number'
  }

  const roundIncomingValueToPrecision = (
    value: string | number | null | undefined,
  ) => {
    if (isNil(value) || isNanValue(value)) return value

    if (typeof value === 'number') {
      value = toNumericString(value)
    }

    if (_valueIsNumericString && typeof decimalScale === 'number') {
      return roundToPrecision(value, decimalScale, Boolean(fixedDecimalScale))
    }

    return value
  }

  const [{ numAsString, formattedValue }, _onValueChange] = useInternalValues(
    roundIncomingValueToPrecision(value),
    roundIncomingValueToPrecision(defaultValue),
    Boolean(_valueIsNumericString),
    _format,
    _removeFormatting,
    onValueChange,
  )

  const _onKeyDown: InputAttributes['onKeyDown'] = (e) => {
    const el = e.target as HTMLInputElement | HTMLTextAreaElement
    const { key } = e
    const { selectionStart, selectionEnd, value = '' } = el
    if (!selectionStart) return
    if (selectionStart !== selectionEnd) {
      onKeyDown(e)
      return
    }

    if (
      key === 'Backspace' &&
      value[0] === '-' &&
      selectionStart === prefix.length + 1 &&
      allowNegative
    ) {
      setCaretPosition(el, 1)
    }

    const { decimalSeparator, allowedDecimalSeparators } = getSeparators(props)
    const isStaticSeparator =
      staticSeparator &&
      allowedDecimalSeparators.includes(key) &&
      formattedValue.includes(decimalSeparator)
    if (isStaticSeparator) {
      e.preventDefault()
      return
    }
    if (
      key === 'Backspace' &&
      value[selectionStart - 1] === decimalSeparator &&
      decimalScale &&
      fixedDecimalScale
    ) {
      setCaretPosition(el, selectionStart - 1)
      e.preventDefault()
    }

    if (
      allowedDecimalSeparators?.includes(key) &&
      value[selectionStart] === decimalSeparator
    ) {
      setCaretPosition(el, selectionStart + 1)
    }

    const _thousandSeparator =
      thousandSeparator === true ? ',' : thousandSeparator

    if (
      key === 'Backspace' &&
      value[(selectionStart as number) - 1] === _thousandSeparator
    ) {
      setCaretPosition(el, (selectionStart as number) - 1)
    }

    if (
      key === 'Delete' &&
      value[selectionStart as number] === _thousandSeparator
    ) {
      setCaretPosition(el, (selectionStart as number) + 1)
    }

    onKeyDown(e)
  }

  const _onBlur: InputAttributes['onBlur'] = (e) => {
    let _value = numAsString

    if (!/\d/g.test(_value)) {
      _value = ''
    }

    if (!allowLeadingZeros) {
      _value = fixLeadingZero(_value) as string
    }

    if (fixedDecimalScale && decimalScale) {
      _value = roundToPrecision(_value, decimalScale, fixedDecimalScale)
    }

    if (_value !== numAsString) {
      const formattedValue = format(_value, props)
      _onValueChange(
        {
          formattedValue,
          value: _value,
          floatValue: Number.parseFloat(_value),
        },
        {
          event: e,
          source: SourceType.event,
        },
      )
    }

    onBlur(e)
  }

  const isValidInputCharacter = (inputChar: string) => {
    if (inputChar === decimalSeparator) return true
    return charIsNumber(inputChar)
  }

  return {
    ...(restProps as NumberFormatBaseProps<BaseType>),
    value: formattedValue,
    valueIsNumericString: false,
    isValidInputCharacter,
    onValueChange: _onValueChange,
    format: _format,
    removeFormatting: _removeFormatting,
    getCaretBoundary: (formattedValue: string) =>
      getCaretBoundary(formattedValue, props),
    onKeyDown: _onKeyDown,
    onBlur: _onBlur,
    decimalSeparator,
  }
}
